import React from 'react';
import { Link, navigate } from 'gatsby';
import { gsap } from 'gsap';
import { CSSPlugin } from 'gsap/CSSPlugin';

import { SLIDE_LIST } from '../maps';
import Menu from '../components/menu';
import './container.scss';

import { getSlugByIndex } from '../helper/slug-helper';
import { NewContent } from '../components/new-content';
import DownloadButton from './download-button';

import { MAX_SLIDE_NUMBER, BOOTSTRAP_LARGE_SCREEN_MIN } from '../constants';

// SVGs
import Bars from '../images/icons/bars-regular.svg';
import Left from '../images/icons/chevron-left-regular.svg';
import Right from '../images/icons/chevron-right-regular.svg';
import Play from '../images/icons/play-regular.svg';
import Pause from '../images/icons/pause-regular.svg';

export class Container extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isAutoplay: false,
      timer: -100,
    };

    this.containerRef = React.createRef();
    this.containerResizeWrapperRef = React.createRef();
  }

  componentDidMount() {
    const { index } = this.props;
    window.sessionStorage.setItem('previous_slide', getSlugByIndex(index));

    // Prevent webpack from tree shaking the CSSPlugin: https://greensock.com/docs/v2/NPMUsage
    gsap.registerPlugin(CSSPlugin);

    this.resizeContainerToWindow();
    window.addEventListener('resize', this.resizeContainerToWindow);

    if (window.localStorage.getItem('autoplay') === 'true') {
      this.startAutoplay();
    }

    document.addEventListener('keydown', this.handleMenuEscape);
  }

  componentDidUpdate(prev) {
    const { index, openMenu } = this.props;
    window.sessionStorage.setItem('previous_slide', getSlugByIndex(index));

    if (prev && prev.openMenu !== openMenu) {
      this.setState({
        isMenuOpen: openMenu,
      });
    }
  }

  componentWillUnmount() {
    this.cleanupInterval();
    window.removeEventListener('resize', this.resizeContainerToWindow);
    document.removeEventListener('keydown', this.handleMenuEscape);
  }

  getIOSWindowHeight = () => {
    const ZoomLevel = document.documentElement.clientWidth / window.innerWidth;

    return window.innerHeight * ZoomLevel;
  };

  resizeContainerToWindow = () => {
    const container = this.containerRef.current;
    const containerResizeWrapper = this.containerResizeWrapperRef.current;
    const windowWidth = document.documentElement.clientWidth;
    const bottomOffsetPx = windowWidth < BOOTSTRAP_LARGE_SCREEN_MIN ? 30 : 60;
    const windowHeight = this.getIOSWindowHeight();

    const scale = Math.min(
      windowWidth / container.offsetWidth,
      (windowHeight - bottomOffsetPx) / container.offsetHeight
    );

    containerResizeWrapper.style.height = `${windowHeight - bottomOffsetPx}px`;
    container.style.transform = `translate(-50%, -50%) scale(${scale})`;

    setTimeout(() => {
      container.style.opacity = 1;
    }, 25);
  };

  startAutoplay = () => {
    this.setState({ isAutoplay: true });
    window.localStorage.setItem('autoplay', 'true');

    this.timerInterval = setInterval(() => {
      this.setState((prevState) => ({ timer: prevState.timer + 1 }));
    }, 100);

    this.redirectInterval = setTimeout(async () => {
      const { index } = this.props;
      const next = index + 1;
      const nextSlug = next >= MAX_SLIDE_NUMBER ? SLIDE_LIST[1].slug : SLIDE_LIST[next].slug;

      await navigate(`/presentation/${nextSlug}`);
      this.setState({ timer: -100 });
    }, 10000);

    // If user starts autoplay on the start page, go directly to the first slide
    if (this.props.index === 0) {
      navigate(SLIDE_LIST[1].slug);
      this.setState({ timer: -100 });
    }
  };

  stopAutoplay = () => {
    this.setState({ isAutoplay: false, timer: -100 });
    window.localStorage.removeItem('autoplay');
    this.cleanupInterval();
  };

  toggleMenu = () => {
    this.setState((prevState) => ({ isMenuOpen: !prevState.isMenuOpen }));
    this.stopAutoplay();
  };

  closeMenu = () => {
    this.setState({ isMenuOpen: false });
  };

  handleMenuEscape = (e) => {
    if (e.key === 'Escape' && this.state.isMenuOpen) {
      this.closeMenu();
    }
  };

  cleanupInterval = () => {
    clearTimeout(this.redirectInterval);
    clearInterval(this.timerInterval);

    this.redirectInterval = null;
    this.timerInterval = null;
  };

  render() {
    const { isMenuOpen, isAutoplay, timer } = this.state;
    const { index, children } = this.props;

    // Slide related variables
    const previous = index - 1;
    const next = index + 1;

    return (
      <div className="overflow-hidden" ref={this.containerResizeWrapperRef}>
        <div className="Container__progress" style={{ transform: `translateX(${timer}%)` }} />
        <div className="Container" ref={this.containerRef}>
          {previous >= 0 ? (
            <Link
              to={previous === 0 ? '/presentation' : `/presentation/${getSlugByIndex(previous)}`}
              onClick={this.stopAutoplay}
              className="Container__control-arrow"
            >
              <Left height={20} />
            </Link>
          ) : null}
          <div className="Container__content">
            {children}
            <NewContent index={index} />
          </div>
          <div className="Container__controls d-flex justify-content-center">
            <div className="btn-group">
              {previous >= 0 ? (
                <Link
                  to={previous === 0 ? '/presentation' : `/presentation/${getSlugByIndex(previous)}`}
                  onClick={this.stopAutoplay}
                  className={`Container__control-element btn btn-primary px-3 py-2`}
                  title="Vorherige Folie"
                >
                  <Left height={20} />
                </Link>
              ) : null}
              <DownloadButton index={index} />
              <button
                onClick={isAutoplay === false ? this.startAutoplay : this.stopAutoplay}
                className="Container__control-element btn btn-primary"
                title={isAutoplay === false ? 'Automatisch abspielen' : 'Automatisches Abspielen stoppen'}
              >
                {isAutoplay === false ? <Play height={20} /> : <Pause height={20} />}
              </button>
              <button onClick={this.toggleMenu} className="Container__control-element btn btn-primary" title="Menü">
                <Bars height={20} />
              </button>
              <Link
                to={next > MAX_SLIDE_NUMBER ? '/presentation' : `/presentation/${getSlugByIndex(next)}`}
                onClick={this.stopAutoplay}
                className="Container__control-element btn btn-primary px-3 py-2"
                title="Nächste Folie"
              >
                <Right height={20} />
              </Link>
            </div>
          </div>
          <Menu isOpen={isMenuOpen} toggleMenu={this.toggleMenu} />
          <Link
            to={next > MAX_SLIDE_NUMBER ? '/presentation' : `/presentation/${getSlugByIndex(next)}`}
            onClick={this.stopAutoplay}
            className="Container__control-arrow Container__control-arrow--right"
          >
            <Right height={20} />
          </Link>
        </div>
      </div>
    );
  }
}
